import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Modal, Button, Spinner, Badge, Alert, Accordion, Table, ProgressBar, ButtonGroup } from 'react-bootstrap';
import { FaRobot, FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaSync, FaHistory, FaFileExcel } from 'react-icons/fa';
import { getFirestore, doc, getDoc, collection, query, where, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import firebaseApp from '../../../firebase/credenciales';
import { auditarLote } from '../../../services/geminiService';
import { prepararContexto, crearLotes, consolidarResultados } from '../utils/caliAuditUtils';

const db = getFirestore(firebaseApp);

const CaliAuditModal = ({ show, onHide, planilla, usuario, onConfirmarAprobacion }) => {
    const [loading, setLoading] = useState(false);
    const [cargandoCache, setCargandoCache] = useState(false);
    const [progreso, setProgreso] = useState({ actual: 0, total: 0, mensaje: '' });
    const [resultadoFinal, setResultadoFinal] = useState(null);
    const [errorSistema, setErrorSistema] = useState(null);
    const [usandoCache, setUsandoCache] = useState(false);
    const [fechaCache, setFechaCache] = useState(null);
    const [filtroTipo, setFiltroTipo] = useState('TODOS'); // TODOS, CRITICO, ADVERTENCIA

    // Ref para evitar ejecuciones duplicadas
    const auditoriaEnCurso = useRef(false);
    const planillaIdRef = useRef(null);

    // Función para filtrar observaciones
    const observacionesFiltradas = () => {
        if (!resultadoFinal?.observaciones) return [];
        if (filtroTipo === 'TODOS') return resultadoFinal.observaciones;
        return resultadoFinal.observaciones.filter(obs => obs.tipo === filtroTipo);
    };

    // Función para exportar a Excel
    const exportarObservacionesExcel = () => {
        const observaciones = observacionesFiltradas();
        if (observaciones.length === 0) {
            alert('No hay observaciones para exportar');
            return;
        }

        // Crear CSV simple
        let csv = 'Empleado,DNI,Tipo,Mensaje\n';
        observaciones.forEach(obs => {
            const mensaje = (obs.mensaje || '').replace(/"/g, '""'); // Escapar comillas
            csv += `"${obs.nombres || ''}","${obs.dni || ''}","${obs.tipo}","${mensaje}"\n`;
        });

        // Descargar
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `auditoria_${planilla?.periodo || 'planilla'}_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
    };

    // Verificar si hay análisis en caché
    const verificarCache = useCallback(async () => {
        if (!planilla?.id || !usuario?.ruc) return null;

        try {
            setCargandoCache(true);
            const cacheRef = doc(db, 'cali_reviews', `${usuario.ruc}_${planilla.id}`);
            const cacheSnap = await getDoc(cacheRef);

            if (cacheSnap.exists()) {
                const data = cacheSnap.data();
                const fechaAnalisis = data.fecha_revision?.toDate();

                // 1. Validar si la planilla fue modificada DESPUÉS del análisis
                if (planilla.updated_at && fechaAnalisis) {
                    const fechaModificacionPlanilla = planilla.updated_at.toDate ? planilla.updated_at.toDate() : new Date(planilla.updated_at);
                    if (fechaModificacionPlanilla > fechaAnalisis) {
                        console.log("⚠️ Caché invalidado: Planilla modificada post-auditoría.");
                        return null;
                    }
                }

                const edadHoras = fechaAnalisis ? (Date.now() - fechaAnalisis.getTime()) / (1000 * 60 * 60) : 999;

                // Caché válido por 24 horas
                if (edadHoras < 24 && data.analisis) {
                    setResultadoFinal(data.analisis);
                    setUsandoCache(true);
                    setFechaCache(fechaAnalisis);
                    return data.analisis;
                }
            }
            return null;
        } catch (error) {
            console.error("Error verificando caché:", error);
            return null;
        } finally {
            setCargandoCache(false);
        }
    }, [planilla?.id, usuario?.ruc]);

    const guardarEnCache = async (resultado) => {
        if (!planilla?.id || !usuario?.ruc) return;

        try {
            const cacheRef = doc(db, 'cali_reviews', `${usuario.ruc}_${planilla.id}`);
            await setDoc(cacheRef, {
                planillaId: planilla.id,
                ruc: usuario.ruc,
                analisis: resultado,
                fecha_revision: serverTimestamp(),
                version_cali: "3.5.0"
            });
            console.log("✅ Análisis guardado en caché");
        } catch (error) {
            console.error("⚠️ Error guardando caché:", error);
            // No bloquear el flujo si falla el guardado
        }
    };

    const iniciarAuditoria = useCallback(async (forzar = false) => {
        // Prevenir ejecuciones duplicadas
        if (auditoriaEnCurso.current) {
            console.log("⚠️ Auditoría ya en curso, ignorando...");
            return;
        }

        // Si hay caché válido y no se fuerza, no re-ejecutar
        if (!forzar && resultadoFinal && usandoCache) {
            console.log("✓ Usando resultado en caché");
            return;
        }

        auditoriaEnCurso.current = true;
        setLoading(true);
        setResultadoFinal(null);
        setErrorSistema(null);
        setUsandoCache(false);
        setProgreso({ actual: 0, total: 100, mensaje: 'Iniciando Cali...' });

        try {
            // 1. Configuración
            setProgreso({ actual: 10, total: 100, mensaje: 'Leyendo reglas de negocio...' });

            const configRef = doc(db, "pl_config", usuario.ruc);
            const regimenesRef = doc(db, "pl_regimenes_laborales", usuario.ruc);

            const [configSnap, regimenesSnap] = await Promise.all([
                getDoc(configRef),
                getDoc(regimenesRef)
            ]);

            if (!configSnap.exists() || !regimenesSnap.exists()) {
                throw new Error("No se encontró la configuración o regímenes para auditar.");
            }

            const contexto = prepararContexto(configSnap.data(), regimenesSnap.data());
            // Agregar RUC y periodo para obtener historial
            contexto.ruc = usuario.ruc;
            contexto.periodo = planilla.periodo;

            // 2. Detalle de planilla
            setProgreso({ actual: 20, total: 100, mensaje: 'Obteniendo detalles de la planilla...' });

            const q = query(
                collection(db, "pl_planilla_detalle"),
                where("planilla_id", "==", planilla.id)
            );
            const querySnapshot = await getDocs(q);
            const detalles = [];
            querySnapshot.forEach((doc) => detalles.push(doc.data()));

            if (detalles.length === 0) {
                throw new Error("La planilla no tiene empleados para auditar.");
            }

            // 3. Crear lotes
            const loteTamano = 10; // Reducido para mejor control
            const lotes = crearLotes(detalles, loteTamano);
            const totalLotes = lotes.length;

            console.log(`📦 Procesando ${detalles.length} empleados en ${totalLotes} lotes`);

            const resultadosParciales = [];
            let erroresLotes = 0;

            // 4. Procesar lotes con reintentos
            for (let i = 0; i < totalLotes; i++) {
                const progresoActual = 30 + Math.floor((i / totalLotes) * 65);
                setProgreso({
                    actual: progresoActual,
                    total: 100,
                    mensaje: `Analizando lote ${i + 1} de ${totalLotes} con IA...`
                });

                let intentos = 0;
                let exito = false;

                // Reintentar hasta 3 veces por lote
                while (intentos < 3 && !exito) {
                    try {
                        const resultadoLote = await auditarLote(contexto, lotes[i]);
                        resultadosParciales.push(resultadoLote);
                        exito = true;
                    } catch (error) {
                        intentos++;
                        console.error(`❌ Error en lote ${i + 1}, intento ${intentos}:`, error.message);

                        if (intentos >= 3) {
                            erroresLotes++;
                            // Agregar resultado fallido para no perder el tracking
                            resultadosParciales.push({
                                aprobado: false,
                                metricas: {
                                    lotes_procesados: 1,
                                    errores_criticos: lotes[i].length, // Todos los empleados del lote
                                    advertencias: 0
                                },
                                observaciones: lotes[i].map(emp => ({
                                    nombres: emp.nombres || 'N/A',
                                    dni: emp.dni || emp.numero_documento,
                                    tipo: 'CRITICO',
                                    mensaje: `Error procesando lote: ${error.message.substring(0, 100)}`
                                }))
                            });
                        } else {
                            // Esperar 2 segundos antes de reintentar
                            await new Promise(resolve => setTimeout(resolve, 2000));
                        }
                    }
                }
            }

            // 5. Consolidar
            setProgreso({ actual: 95, total: 100, mensaje: 'Consolidando reporte...' });
            const reporte = consolidarResultados(resultadosParciales);

            // Agregar metadatos
            reporte.metadata = {
                total_empleados: detalles.length,
                total_lotes: totalLotes,
                errores_procesamiento: erroresLotes,
                fecha_analisis: new Date().toISOString()
            };

            setProgreso({ actual: 100, total: 100, mensaje: '¡Análisis completado!' });
            setResultadoFinal(reporte);

            // Guardar en caché de forma asíncrona
            guardarEnCache(reporte);

        } catch (error) {
            console.error("💥 Error crítico en auditoría:", error);
            setErrorSistema(error.message);
        } finally {
            setLoading(false);
            auditoriaEnCurso.current = false;
        }
    }, [planilla, usuario, resultadoFinal, usandoCache]);

    // Efecto para cargar caché y auto-iniciar auditoría
    useEffect(() => {
        if (show && planilla?.id) {
            if (planillaIdRef.current !== planilla.id) {
                planillaIdRef.current = planilla.id;
                setResultadoFinal(null);
                setErrorSistema(null);
                setUsandoCache(false);

                // Verificar caché
                verificarCache().then(cacheResult => {
                    if (!cacheResult) {
                        console.log('⚠️ No hay caché, iniciando auditoría automática...');
                        iniciarAuditoria(false);
                    }
                });
            }
        } else if (!show) {
            planillaIdRef.current = null;
        }
    }, [show, planilla?.id, verificarCache]);


    const getStatusIcon = () => {
        if (errorSistema) return <FaTimesCircle className="text-danger" size={40} />;
        if (cargandoCache) return <Spinner animation="border" variant="primary" />;
        if (!resultadoFinal) return <FaRobot className="text-secondary" size={40} />;
        if (resultadoFinal.aprobado) return <FaCheckCircle className="text-success" size={40} />;
        return <FaExclamationTriangle className="text-warning" size={40} />;
    };

    const getTituloEstado = () => {
        if (cargandoCache) return 'Verificando análisis previo...';
        if (loading) return 'Analizando Planilla...';
        if (errorSistema) return 'Error en Auditoría';
        if (usandoCache) return '✓ Análisis en caché (válido 24h)';
        if (resultadoFinal?.aprobado) return '¡Planilla Aprobada por Cali!';
        return 'Se han detectado observaciones';
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            backdrop="static"
            keyboard={!loading}
            centered
        >
            <Modal.Header closeButton={!loading && !cargandoCache}>
                <Modal.Title className="d-flex align-items-center">
                    <FaRobot className="me-2 text-primary" />
                    Auditoría IA - Cali
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Estado principal */}
                <div className="text-center mb-4">
                    {getStatusIcon()}
                    <h5 className="mt-3">{getTituloEstado()}</h5>

                    {usandoCache && fechaCache && (
                        <small className="text-muted d-block mt-2">
                            <FaHistory className="me-1" />
                            Análisis del {fechaCache.toLocaleString('es-PE')}
                        </small>
                    )}
                </div>

                {/* Barra de progreso */}
                {loading && (
                    <div className="mb-3">
                        <ProgressBar
                            animated
                            now={progreso.actual}
                            label={`${Math.round(progreso.actual)}%`}
                            variant={progreso.actual < 50 ? 'info' : progreso.actual < 90 ? 'primary' : 'success'}
                        />
                        <p className="text-center text-muted mt-2 small">
                            {progreso.mensaje}
                        </p>
                    </div>
                )}

                {/* Error del sistema */}
                {errorSistema && (
                    <Alert variant="danger">
                        <Alert.Heading>
                            <FaTimesCircle className="me-2" />
                            Error en el Sistema
                        </Alert.Heading>
                        <p>{errorSistema}</p>
                        <hr />
                        <div className="d-flex justify-content-between">
                            <small>Puede intentar aprobar manualmente si considera que es un error temporal.</small>
                            <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => iniciarAuditoria(true)}
                            >
                                <FaSync className="me-1" /> Reintentar
                            </Button>
                        </div>
                    </Alert>
                )}

                {/* Resultados */}
                {resultadoFinal && (
                    <div className="resultados-auditoria">
                        {/* Métricas rápidas */}
                        <div className="d-flex justify-content-around mb-3 flex-wrap gap-2">
                            <Badge bg="info" className="py-2 px-3">
                                Empleados: {resultadoFinal.metadata?.total_empleados || 0}
                            </Badge>
                            <Badge bg="primary" className="py-2 px-3">
                                Lotes: {resultadoFinal.metricas?.lotes_procesados || 0}
                            </Badge>
                            <Badge
                                bg={resultadoFinal.metricas?.errores_criticos > 0 ? "danger" : "success"}
                                className="py-2 px-3"
                            >
                                Errores: {resultadoFinal.metricas?.errores_criticos || 0}
                            </Badge>
                            <Badge bg="warning" text="dark" className="py-2 px-3">
                                Advertencias: {resultadoFinal.metricas?.advertencias || 0}
                            </Badge>
                        </div>

                        {/* Observaciones */}
                        {resultadoFinal.observaciones?.length > 0 ? (
                            <>
                                {/* Filtros y Acciones */}
                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                    <ButtonGroup size="sm">
                                        <Button
                                            variant={filtroTipo === 'TODOS' ? 'primary' : 'outline-primary'}
                                            onClick={() => setFiltroTipo('TODOS')}
                                        >
                                            Todos ({resultadoFinal.observaciones.length})
                                        </Button>
                                        <Button
                                            variant={filtroTipo === 'CRITICO' ? 'danger' : 'outline-danger'}
                                            onClick={() => setFiltroTipo('CRITICO')}
                                        >
                                            Críticos ({resultadoFinal.metricas?.errores_criticos || 0})
                                        </Button>
                                        <Button
                                            variant={filtroTipo === 'ADVERTENCIA' ? 'warning' : 'outline-warning'}
                                            onClick={() => setFiltroTipo('ADVERTENCIA')}
                                        >
                                            Advertencias ({resultadoFinal.metricas?.advertencias || 0})
                                        </Button>
                                    </ButtonGroup>

                                    <Button
                                        size="sm"
                                        variant="outline-success"
                                        onClick={exportarObservacionesExcel}
                                    >
                                        <FaFileExcel className="me-1" /> Exportar a Excel
                                    </Button>
                                </div>

                                <Accordion defaultActiveKey="0">
                                    <Accordion.Item eventKey="0">
                                        <Accordion.Header>
                                            <strong>
                                                Ver Detalle de Observaciones ({observacionesFiltradas().length})
                                            </strong>
                                        </Accordion.Header>
                                        <Accordion.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                            <Table size="sm" responsive striped hover>
                                                <thead className="sticky-top bg-white">
                                                    <tr>
                                                        <th style={{ width: '30%' }}>Empleado</th>
                                                        <th style={{ width: '15%' }}>Tipo</th>
                                                        <th style={{ width: '55%' }}>Mensaje</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {observacionesFiltradas()
                                                        .sort((a, b) => {
                                                            // CRÍTICOS primero
                                                            if (a.tipo === 'CRITICO' && b.tipo !== 'CRITICO') return -1;
                                                            if (a.tipo !== 'CRITICO' && b.tipo === 'CRITICO') return 1;
                                                            // Luego por nombre
                                                            return (a.nombres || '').localeCompare(b.nombres || '');
                                                        })
                                                        .map((obs, idx) => (
                                                            <tr key={idx}>
                                                                <td>
                                                                    <div className="fw-bold">{obs.nombres}</div>
                                                                    <small className="text-muted">{obs.dni}</small>
                                                                </td>
                                                                <td>
                                                                    <Badge bg={obs.tipo === 'CRITICO' ? 'danger' : 'warning'}>
                                                                        {obs.tipo}
                                                                    </Badge>
                                                                </td>
                                                                <td className="small">{obs.mensaje}</td>
                                                            </tr>
                                                        ))
                                                    }
                                                </tbody>
                                            </Table>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </>
                        ) : (
                            <Alert variant="success">
                                <FaCheckCircle className="me-2" />
                                <strong>¡Todo en orden!</strong> No se encontraron anomalías en los cálculos.
                            </Alert>
                        )}

                        {/* Recomendación */}
                        {!resultadoFinal.aprobado && (
                            <Alert variant="warning" className="mt-3 mb-0">
                                <FaExclamationTriangle className="me-2" />
                                <strong>Recomendación:</strong> Cali sugiere revisar las observaciones críticas antes de aprobar esta planilla.
                            </Alert>
                        )}

                        {/* Opción para forzar re-análisis */}
                        {usandoCache && (
                            <div className="mt-3 text-center">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => iniciarAuditoria(true)}
                                    disabled={loading}
                                >
                                    <FaSync className="me-1" /> Ejecutar nuevo análisis
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={onHide}
                    disabled={loading || cargandoCache}
                >
                    {(resultadoFinal?.aprobado || errorSistema) ? 'Cerrar' : 'Cancelar'}
                </Button>

                {!loading && !cargandoCache && (
                    <Button
                        variant={resultadoFinal?.aprobado ? "success" : "warning"}
                        onClick={() => onConfirmarAprobacion(planilla.id)}
                    >
                        {resultadoFinal?.aprobado ? (
                            <>
                                <FaCheckCircle className="me-1" /> Confirmar Aprobación
                            </>
                        ) : (
                            <>Aprobar de todos modos</>
                        )}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default CaliAuditModal;